import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import useSettings from "../../settings/useSettings";

const DEFAULT_SITE_ORIGIN = "https://slcupcakery.com";
const DEFAULT_IMAGE = "/logo512.png";

function siteOrigin(settings) {
  return (
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    DEFAULT_SITE_ORIGIN
  ).replace(/\/+$/, "");
}

function absoluteUrl(origin, path) {
  if (!path) return origin;
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function setTag(selector, createTag, attrs = {}, text = "") {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = createTag();
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") tag.removeAttribute(key);
    else tag.setAttribute(key, value);
  });
  if (text) tag.textContent = text;
  return tag;
}

function businessJsonLd(settings, origin) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Bakery",
    name: settings?.business_name || "SugaLumps Cupcakery & Sweet Treats",
    url: origin,
    logo: absoluteUrl(origin, settings?.logo_url || DEFAULT_IMAGE),
    image: absoluteUrl(origin, DEFAULT_IMAGE),
    telephone: settings?.business_phone || undefined,
    email: settings?.business_email || undefined,
    areaServed: settings?.delivery_summary?.service_area_name || "Silver Spring and the DMV",
    sameAs: [settings?.instagram_url, settings?.facebook_url].filter(Boolean),
  };
  Object.keys(data).forEach((key) => data[key] === undefined && delete data[key]);
  if (!data.sameAs.length) delete data.sameAs;
  return data;
}

export default function PublicSEO({ title, description, canonicalPath, robots = "index,follow", image = DEFAULT_IMAGE, structuredData }) {
  const { settings } = useSettings();
  const location = useLocation();
  const origin = siteOrigin(settings);
  const canonical = absoluteUrl(origin, canonicalPath || location.pathname || "/");
  const imageUrl = absoluteUrl(origin, image);
  const jsonLd = useMemo(() => structuredData === "business" ? businessJsonLd(settings, origin) : structuredData, [structuredData, settings, origin]);

  useEffect(() => {
    document.title = title;
    setTag('meta[name="description"]', () => document.createElement("meta"), { name: "description", content: description });
    setTag('meta[name="robots"]', () => document.createElement("meta"), { name: "robots", content: robots });
    setTag('link[rel="canonical"]', () => {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      return link;
    }, { href: canonical });
    setTag('meta[property="og:title"]', () => document.createElement("meta"), { property: "og:title", content: title });
    setTag('meta[property="og:description"]', () => document.createElement("meta"), { property: "og:description", content: description });
    setTag('meta[property="og:url"]', () => document.createElement("meta"), { property: "og:url", content: canonical });
    setTag('meta[property="og:image"]', () => document.createElement("meta"), { property: "og:image", content: imageUrl });
    setTag('meta[property="og:type"]', () => document.createElement("meta"), { property: "og:type", content: "website" });
    setTag('meta[name="twitter:card"]', () => document.createElement("meta"), { name: "twitter:card", content: "summary_large_image" });
    setTag('meta[name="twitter:title"]', () => document.createElement("meta"), { name: "twitter:title", content: title });
    setTag('meta[name="twitter:description"]', () => document.createElement("meta"), { name: "twitter:description", content: description });
    setTag('meta[name="twitter:image"]', () => document.createElement("meta"), { name: "twitter:image", content: imageUrl });

    const existingJsonLd = document.head.querySelector('script[data-public-seo="jsonld"]');
    if (existingJsonLd) existingJsonLd.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-public-seo", "jsonld");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, robots, canonical, imageUrl, jsonLd]);

  return null;
}
